
import { useMemo } from 'react';

export interface ShippingMethod {
  id: string;
  label: string;
  description: string;
  price: number;
  minDays: number;
  maxDays: number;
  icon: string;
}

export const shippingMethods: ShippingMethod[] = [
  {
    id: 'standard',
    label: 'Standard Shipping',
    description: 'Reliable delivery at no extra cost',
    price: 0,
    minDays: 5,
    maxDays: 8,
    icon: 'ri-truck-line',
  },
  {
    id: 'express',
    label: 'Express Shipping',
    description: 'Faster delivery for your order',
    price: 9.99,
    minDays: 2,
    maxDays: 4,
    icon: 'ri-rocket-line',
  },
  {
    id: 'overnight',
    label: 'Overnight Shipping',
    description: 'Next business day delivery',
    price: 19.99,
    minDays: 1,
    maxDays: 1,
    icon: 'ri-flashlight-line',
  },
];

function addBusinessDays(date: Date, days: number): Date {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dayOfWeek = result.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return result;
}

function formatDeliveryDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

interface ShippingMethodSelectorProps {
  selectedMethod: string;
  onSelect: (method: ShippingMethod) => void;
  orderDate?: Date;
}

export function ShippingMethodSelector({
  selectedMethod,
  onSelect,
  orderDate,
}: ShippingMethodSelectorProps) {
  const baseDate = orderDate || new Date();

  return (
    <div className="space-y-3">
      {shippingMethods.map((method) => {
        const minDate = addBusinessDays(baseDate, method.minDays);
        const maxDate = addBusinessDays(baseDate, method.maxDays);
        const isSelected = selectedMethod === method.id;
        const dateRange =
          method.minDays === method.maxDays
            ? formatDeliveryDate(minDate)
            : `${formatDeliveryDate(minDate)} – ${formatDeliveryDate(maxDate)}`;

        return (
          <button
            key={method.id}
            type="button"
            onClick={() => onSelect(method)}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
              isSelected
                ? 'border-[#2E7D32] bg-[#F1F8E9]'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-[#2E7D32] text-white' : 'bg-gray-100 text-gray-400'
              }`}
            >
              <i className={`${method.icon} text-lg`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p
                  className={`text-sm font-semibold ${
                    isSelected ? 'text-[#1B5E20]' : 'text-gray-900'
                  }`}
                >
                  {method.label}
                </p>
                {method.id === 'express' && (
                  <span className="text-[10px] font-bold tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                    POPULAR
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{method.description}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <p
                className={`text-sm font-semibold ${
                  isSelected ? 'text-[#2E7D32]' : 'text-gray-900'
                }`}
              >
                {method.price === 0 ? 'FREE' : `$${method.price.toFixed(2)}`}
              </p>
              <p className="text-[11px] text-gray-400 mt-0.5 whitespace-nowrap">{dateRange}</p>
            </div>
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'border-[#2E7D32]' : 'border-gray-300'
              }`}
            >
              {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#2E7D32]"></div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface EstimatedDeliveryBadgeProps {
  shippingMethodId?: string;
  orderDate?: string;
  compact?: boolean;
}

export function EstimatedDeliveryBadge({
  shippingMethodId = 'standard',
  orderDate,
  compact = false,
}: EstimatedDeliveryBadgeProps) {
  const { dateRange, method } = useMemo(() => {
    const m = shippingMethods.find((s) => s.id === shippingMethodId) || shippingMethods[0];
    const base = orderDate ? new Date(orderDate) : new Date();
    const minDate = addBusinessDays(base, m.minDays);
    const maxDate = addBusinessDays(base, m.maxDays);
    const range =
      m.minDays === m.maxDays
        ? formatDeliveryDate(minDate)
        : `${formatDeliveryDate(minDate)} – ${formatDeliveryDate(maxDate)}`;
    return { dateRange: range, method: m };
  }, [shippingMethodId, orderDate]);

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <i className="ri-calendar-check-line text-[#2E7D32]"></i>
        <span className="text-gray-500">Est. delivery:</span>
        <span className="font-semibold text-gray-900">{dateRange}</span>
      </div>
    );
  }

  return (
    <div className="bg-[#F1F8E9] border border-[#C8E6C9] rounded-xl p-4 flex items-center gap-4">
      <div className="w-10 h-10 bg-[#2E7D32] rounded-lg flex items-center justify-center flex-shrink-0">
        <i className="ri-calendar-check-line text-lg text-white"></i>
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-[#1B5E20]">Estimated Delivery</p>
        <p className="text-xs text-gray-600 mt-0.5">
          {dateRange} via <span className="font-medium">{method.label}</span>
        </p>
      </div>
      <div className="w-8 h-8 flex items-center justify-center text-[#2E7D32]">
        <i className={`${method.icon} text-xl`}></i>
      </div>
    </div>
  );
}
